import { API_CONFIG } from '../../config/api.config';
import { cloudTTSService } from './CloudTTSService';

export interface TTSOptions {
  voice?: SpeechSynthesisVoice | string;
  speed?: number;
  pitch?: number;
  volume?: number;
  language?: string;
  preferredGender?: 'male' | 'female' | 'any';
  preferredProvider?: 'microsoft' | 'google' | 'any';
  chunkSize?: number;
  chunkDelay?: number;
  maxRetries?: number;
}

export interface TTSVoice {
  name: string;
  lang: string;
  gender: 'male' | 'female';
  rate: number;
  pitch: number;
}

export class TextToSpeechService {
  private static instance: TextToSpeechService;
  private synthesis: SpeechSynthesis;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private isPlaying = false;
  private availableVoices: SpeechSynthesisVoice[] = [];
  private currentVoice: SpeechSynthesisVoice | null = null;
  private currentChunks: string[] = [];
  private currentChunkIndex = 0;

  // Cloud Audio Element for Mobile Compatibility
  private cloudAudio: HTMLAudioElement;

  private constructor() {
    this.synthesis = window.speechSynthesis;
    this.cloudAudio = new Audio();

    this.loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => this.loadVoices();
    }
  }

  public static getInstance(): TextToSpeechService {
    if (!TextToSpeechService.instance) {
      TextToSpeechService.instance = new TextToSpeechService();
    }
    return TextToSpeechService.instance;
  }

  /**
   * Unlocks audio playback on mobile devices.
   * Must be called from a direct user interaction (click/tap).
   */
  public resumeAudio() {
    if (this.cloudAudio) {
      // Play a tiny silent sound to unlock audio
      this.cloudAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAGZGF0YQQAAAAAAA==';
      this.cloudAudio.play().then(() => {
        this.cloudAudio.pause();
        this.cloudAudio.currentTime = 0;
        console.log('🔓 Audio unlocked successfully');
      }).catch(e => {
        console.warn('🔓 Audio unlock failed (normal if no user interaction):', e);
      });
    }
  }

  private loadVoices(): void {
    const voices = this.synthesis.getVoices();
    if (voices.length > 0) {
      this.availableVoices = voices;
      console.log(`✅ Loaded ${voices.length} voices`);
    }
  }

  public getAvailableVoices(): SpeechSynthesisVoice[] {
    return this.availableVoices;
  }

  public getCloudVoices(): SpeechSynthesisVoice[] {
    // Return mock OpenAI voices
    const voices = ['Alloy', 'Echo', 'Fable', 'Onyx', 'Nova', 'Shimmer'];
    return voices.map(v => ({
      name: `OpenAI ${v}`,
      lang: 'en-US',
      localService: false,
      default: false,
      voiceURI: `openai-${v.toLowerCase()}`
    } as SpeechSynthesisVoice));
  }

  public setVoice(voice: SpeechSynthesisVoice) {
    this.currentVoice = voice;
  }

  public getSelectedVoice(): SpeechSynthesisVoice | null {
    return this.currentVoice;
  }

  public stop() {
    this.isPlaying = false;
    this.synthesis.cancel();
    if (this.cloudAudio) {
      this.cloudAudio.pause();
      this.cloudAudio.currentTime = 0;
    }
  }

  public async speak(text: string, options: TTSOptions = {}): Promise<void> {
    this.stop();
    this.isPlaying = true;

    // Split text into chunks if needed (simplified for now)
    const chunks = [text];
    this.currentChunks = chunks;
    this.currentChunkIndex = 0;

    for (const chunk of chunks) {
      if (!this.isPlaying) break;
      await this.speakSingleChunk(chunk, options);
    }

    this.isPlaying = false;
  }

  private async speakSingleChunk(text: string, options: TTSOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      this.currentUtterance = utterance;

      // Set voice based on options
      let selectedVoice: SpeechSynthesisVoice | null = null;
      if (options.voice) {
        if (typeof options.voice === 'string') {
          selectedVoice = this.availableVoices.find(v => v.name === options.voice) || null;
          // Check if it's a cloud voice name
          if (!selectedVoice && options.voice.startsWith('OpenAI')) {
            selectedVoice = { name: options.voice, lang: 'en-US' } as SpeechSynthesisVoice;
          }
        } else {
          selectedVoice = options.voice;
        }
      }

      // CLOUD TTS CHECK
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const hasApiKey = !!import.meta.env.VITE_OPENAI_API_KEY;
      const hasProxy = !!API_CONFIG.openai.proxyUrl;
      const isOpenAIVoice = selectedVoice && selectedVoice.name.startsWith('OpenAI');

      const useCloudTTS = (hasApiKey || hasProxy) && (isMobile || isOpenAIVoice);

      const timeout = setTimeout(() => {
        console.error('TTS timeout');
        this.stop();
        reject(new Error('TTS timeout'));
      }, 30000);

      if (useCloudTTS) {
        console.log(`☁️ Cloud TTS activated (Mobile: ${isMobile}, Voice: ${selectedVoice?.name})...`);
        this.speakWithCloud(text, options)
          .then(() => {
            clearTimeout(timeout);
            resolve();
          })
          .catch((err) => {
            console.warn('⚠️ Cloud TTS failed, falling back to local:', err);
            if (isMobile && !window['hasAlertedTTSError']) {
              const msg = err.message || String(err);
              if (!msg.includes('aborted')) {
                window.alert(`Cloud TTS Error: ${msg}\nProxy: ${hasProxy}`);
                window['hasAlertedTTSError'] = true;
              }
            }
            // Fallback to local
            this.speakLocal(text, options, utterance, resolve, reject, timeout);
          });
        return;
      }

      // Local TTS
      if (selectedVoice && !selectedVoice.name.startsWith('OpenAI')) {
        utterance.voice = selectedVoice;
      }
      this.speakLocal(text, options, utterance, resolve, reject, timeout);
    });
  }

  private speakLocal(text: string, options: TTSOptions, utterance: SpeechSynthesisUtterance, resolve: Function, reject: Function, timeout: any) {
    utterance.rate = options.speed || 1.0;
    utterance.pitch = options.pitch || 1.0;
    utterance.volume = options.volume || 1.0;

    utterance.onend = () => {
      clearTimeout(timeout);
      resolve();
    };
    utterance.onerror = (e) => {
      clearTimeout(timeout);
      reject(e);
    };

    this.synthesis.speak(utterance);
  }

  private async speakWithCloud(text: string, options: TTSOptions): Promise<void> {
    // Determine OpenAI voice
    let openAIVoice: any = 'alloy';
    if (options.voice) {
      const name = typeof options.voice === 'string' ? options.voice : options.voice.name;
      const lower = name.toLowerCase().replace('openai ', '');
      if (['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'].includes(lower)) {
        openAIVoice = lower;
      }
    }

    // 1. Check Cache First (Fastest)
    const cachedBlob = await cloudTTSService.getCachedAudio(text, openAIVoice, options.speed);
    if (cachedBlob) {
        return new Promise((resolve, reject) => {
            console.log('✅ Playing from cache...');
            const url = URL.createObjectURL(cachedBlob);
            this.cloudAudio.src = url;
            this.cloudAudio.onended = () => {
                this.isPlaying = false;
                URL.revokeObjectURL(url);
                resolve();
            };
            this.cloudAudio.onerror = (e) => {
                this.isPlaying = false;
                URL.revokeObjectURL(url);
                reject(e);
            };
            this.isPlaying = true;
            this.cloudAudio.play().catch(reject);
        });
    }

    // 2. Cache Miss - Try getting a direct URL (fast, supports streaming)
    const directUrl = cloudTTSService.getAudioUrl(text, openAIVoice, options.speed);
    
    // Trigger background caching so it's ready next time
    cloudTTSService.getAudio(text, openAIVoice, options.speed).catch(e => console.warn('Background caching failed:', e));

    if (directUrl) {
        return new Promise((resolve, reject) => {
            console.log('☁️ Using direct streaming URL for TTS...');
            this.cloudAudio.src = directUrl;

            this.cloudAudio.onended = () => {
                this.isPlaying = false;
                resolve();
            };

            this.cloudAudio.onerror = (e) => {
                this.isPlaying = false;
                reject(e);
            };

            this.isPlaying = true;
            this.cloudAudio.play().catch(reject);
        });
    }

    // 3. Fallback to full blob if text is too long or GET fails
    const audioBlob = await cloudTTSService.getAudio(text, openAIVoice, options.speed);
    if (!audioBlob) throw new Error('Failed to get audio blob');

    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(audioBlob);
      this.cloudAudio.src = url;

      this.cloudAudio.onended = () => {
        this.isPlaying = false;
        URL.revokeObjectURL(url);
        resolve();
      };

      this.cloudAudio.onerror = (e) => {
        this.isPlaying = false;
        URL.revokeObjectURL(url);
        reject(e);
      };

      this.isPlaying = true;
      this.cloudAudio.play().catch(reject);
    });
  }

  // Helpers
  public getBestVoiceForOptions(options: TTSOptions): SpeechSynthesisVoice | null {
    return this.availableVoices[0] || null;
  }

  public getBestEnglishVoice(): SpeechSynthesisVoice | null {
    const voices = this.getAvailableVoices();
    // Prioritize:
    // 1. Google US English
    // 2. Microsoft US English
    // 3. Any English

    return voices.find(v => v.name.includes('Google') && v.lang.includes('en-US')) ||
      voices.find(v => v.name.includes('Microsoft') && v.lang.includes('en-US')) ||
      voices.find(v => v.lang.includes('en')) ||
      null;
  }

  public getVoiceQuality(voice: SpeechSynthesisVoice): 'excellent' | 'good' | 'fair' | 'poor' {
    if (voice.name.startsWith('OpenAI')) return 'excellent';
    if (voice.name.includes('Google') && (voice.name.includes('Neural') || voice.name.includes('Premium'))) return 'excellent';
    if (voice.name.includes('Natural')) return 'good';
    return 'fair';
  }

  public async speakWord(text: string, options: TTSOptions = {}): Promise<void> {
    return this.speak(text, { ...options, speed: options.speed || 1.0 });
  }

  public async speakSentence(text: string, options: TTSOptions = {}): Promise<void> {
    return this.speak(text, { ...options, speed: options.speed || 0.9 });
  }

  public async speakParagraph(text: string, options: TTSOptions = {}): Promise<void> {
    return this.speak(text, { ...options, speed: options.speed || 0.85 });
  }

  // ==========================================
  // Fix for VoiceSelector compatibility
  // ==========================================

  public getVoices(): SpeechSynthesisVoice[] {
    return this.getAvailableVoices();
  }

  public async ensureVoicesLoaded(): Promise<void> {
    if (this.availableVoices.length > 0) return;

    // Wait for voices to load (max 3 seconds)
    return new Promise((resolve) => {
      let retries = 0;
      const checkInterval = setInterval(() => {
        const voices = this.synthesis.getVoices();
        if (voices.length > 0) {
          this.availableVoices = voices;
          clearInterval(checkInterval);
          resolve();
        } else if (retries > 30) { // 3 seconds timeout
          clearInterval(checkInterval);
          resolve(); // resolve anyway to avoid blocking
        }
        retries++;
      }, 100);
    });
  }

  public async forceReloadVoices(): Promise<void> {
    this.availableVoices = [];
    this.loadVoices();
    // Wait a bit to ensuring loading
    await this.ensureVoicesLoaded();
  }

  public getVoiceGender(voice: SpeechSynthesisVoice): 'male' | 'female' | 'unknown' {
    if (voice.name.includes('Female') || voice.name.includes('Zira') || voice.name.includes('Susan') || voice.name.includes('Julie')) return 'female';
    if (voice.name.includes('Male') || voice.name.includes('David') || voice.name.includes('Mark')) return 'male';
    return 'unknown';
  }
}

export const textToSpeechService = TextToSpeechService.getInstance();
